import { useEffect, useRef } from "react";
import {
  FieldValues,
  Path,
  PathValue,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

type CascadeResetValue = { id?: string } | null | undefined;

type UseCascadeResetParams<
  TFieldValues extends FieldValues,
  TParentField extends Path<TFieldValues>,
  TChildField extends Path<TFieldValues>,
  TParentValue extends CascadeResetValue
> = {
  watch: UseFormWatch<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  parentField: TParentField;
  childField: TChildField;
  getValueId?: (value: TParentValue) => string | undefined;
};

export const useCascadeReset = <
  TFieldValues extends FieldValues,
  TParentField extends Path<TFieldValues>,
  TChildField extends Path<TFieldValues>,
  TParentValue extends CascadeResetValue = Extract<
    PathValue<TFieldValues, TParentField>,
    CascadeResetValue
  >
>({
  watch,
  setValue,
  parentField,
  childField,
  getValueId = (value) => value?.id,
}: UseCascadeResetParams<
  TFieldValues,
  TParentField,
  TChildField,
  TParentValue
>): TParentValue => {
  const watchField = watch(parentField) as TParentValue;
  const previousValue = useRef(watchField);

  useEffect(() => {
    const previous = getValueId(previousValue.current);
    const current = getValueId(watchField);

    if (!!previous && previous !== current) {
      setValue(childField, null as PathValue<TFieldValues, TChildField>);
    }

    previousValue.current = watchField;
  }, [childField, getValueId, setValue, watchField]);

  return watchField;
};
